import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export function avatarSrc(agent) {
  if (!agent?.avatar_url) return null;
  return agent.avatar_url.startsWith("http") ? agent.avatar_url : `${BACKEND_URL}${agent.avatar_url}`;
}

// Map UI language names → BCP-47 codes for browser STT/TTS
export const LANG_CODES = {
  English: "en-US",
  Swedish: "sv-SE",
  Norwegian: "nb-NO",
  Danish: "da-DK",
  Spanish: "es-ES",
  French: "fr-FR",
  German: "de-DE",
  Dutch: "nl-NL",
  Portuguese: "pt-PT",
  Italian: "it-IT",
  Arabic: "ar-SA",
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("senzum_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export async function fetchTTS({ path, text, voiceId }) {
  const token = localStorage.getItem("senzum_token");
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ text, ...(voiceId ? { voice_id: voiceId } : {}) }),
  });
  if (!res.ok) throw new Error(`TTS failed (${res.status})`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function downloadFile(path, filename) {
  const token = localStorage.getItem("senzum_token");
  const res = await fetch(`${API}${path}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`Nedladdning misslyckades (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Stream a chat reply via SSE. Calls onDelta(text) for each chunk and
 * onDone({ escalated, session_id }) at the end.
 */
export async function streamChat({ path, message, sessionId, channel = "chat", imageBase64, ref, accessCode, onDelta, onDone, onError, onStep }) {
  const token = localStorage.getItem("senzum_token");
  try {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ message, session_id: sessionId, channel, ...(imageBase64 ? { image_base64: imageBase64 } : {}), ...(ref ? { ref } : {}), ...(accessCode ? { access_code: accessCode } : {}) }),
    });
    if (res.status === 403) {
      const err = new Error("access_denied"); err.code = 403; throw err;
    }
    if (!res.ok || !res.body) {
      const txt = await res.text().catch(() => "");
      throw new Error(txt || `Request failed (${res.status})`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop();
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith("data:")) continue;
        const payload = JSON.parse(line.slice(5).trim());
        if (payload.step) onStep?.(payload.step);
        if (payload.delta) onDelta?.(payload.delta);
        if (payload.done) onDone?.(payload);
      }
    }
  } catch (e) {
    onError?.(e);
  }
}
